import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, MessageCircle, Phone, X } from 'lucide-react';
import { fundingAuthAPI, getFundingToken } from '@/utils/api';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { formatYuanAmount } from '@/utils/membershipDuration';
import { PURCHASE_INTENT_LIMITS } from '@/constants/purchaseIntent';
import {
  getCartItemLineYuan,
  getCartItemQuantity,
  getCartItemUnitYuan,
  sumRechargeCartQuantity,
  sumRechargeCartYuan,
} from '@/utils/ipeRechargeCart';
import {
  EMPTY_PURCHASE_INTENT_CONTACT,
  PURCHASE_INTENT_SUBMITTED_TOAST,
  buildPurchaseIntentSubmitPayload,
} from '@/utils/purchaseIntentSubmit';
import RechargeScanPayPlaceholder from '@/components/ipe/RechargeScanPayPlaceholder';
import { isScanPayPlaceholderVisible, resolvePayMethodHint } from '@/constants/onlinePaymentFreeze';
import '@/styles/ipe/recharge-cart-checkout-modal.css';

function buildCheckoutSummaryLines(items) {
  return (items || []).map((item, index) => {
    const scope = String(item.scope || 'personal').toLowerCase() === 'team' ? '团队' : '个人';
    const qty = getCartItemQuantity(item);
    const lineYuan = getCartItemLineYuan(item);
    const amount = `¥${formatYuanAmount(lineYuan)}`;
    const qtyPart = qty > 1 ? ` ×${qty}` : '';
    return `${index + 1}. [${scope}] ${item.title || '商品'}（${item.summary || item.productTypeLabel || '—'}）${qtyPart} ${amount}`;
  });
}

function buildCheckoutClipboardText(items, totalYuan) {
  const lines = buildCheckoutSummaryLines(items);
  const totalQty = sumRechargeCartQuantity(items);
  return [
    'IPE 购买意向清单',
    `共 ${totalQty} 件，合计 ¥${formatYuanAmount(totalYuan)}`,
    '',
    ...lines,
    '',
    '请协助完成开通，谢谢。',
  ].join('\n');
}

function BillingScopeTag({ scope }) {
  const isTeam = String(scope || '').toLowerCase() === 'team';
  return (
    <span className={`ipe-recharge-cart-checkout__scope${isTeam ? ' ipe-recharge-cart-checkout__scope--team' : ''}`}>
      {isTeam ? '团队' : '个人'}
    </span>
  );
}

function Field({ id, label, value, onChange, error, hint, placeholder, maxLength, inputMode, autoComplete, disabled }) {
  return (
    <label className="ipe-recharge-cart-checkout__field" htmlFor={id}>
      <span className="ipe-recharge-cart-checkout__label">{label}</span>
      <input
        id={id}
        className={`ipe-recharge-cart-checkout__input${error ? ' ipe-recharge-cart-checkout__input--error' : ''}`}
        value={value}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? <span className="ipe-recharge-cart-checkout__error">{error}</span> : null}
      {!error && hint ? <span className="ipe-recharge-cart-checkout__hint">{hint}</span> : null}
    </label>
  );
}

export default function RechargeCartCheckoutModal({
  open,
  onClose,
  items,
  showToast,
  onSubmitted,
}) {
  const { user } = useIpeAuth();
  const [bootLoading, setBootLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recharge, setRecharge] = useState(null);
  const [contactHint, setContactHint] = useState('');
  const [form, setForm] = useState(EMPTY_PURCHASE_INTENT_CONTACT);
  const [errors, setErrors] = useState({});
  const submittingRef = useRef(false);

  const totalYuan = useMemo(() => sumRechargeCartYuan(items), [items]);
  const totalQty = useMemo(() => sumRechargeCartQuantity(items), [items]);
  const loggedIn = Boolean(user?.id || user?._id || getFundingToken());

  const patchField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const load = useCallback(async () => {
    setBootLoading(true);
    setErrors({});
    setForm(EMPTY_PURCHASE_INTENT_CONTACT);
    try {
      const center = await fundingAuthAPI.getAccountCenter().catch(() => null);
      setRecharge(center?.recharge || {});
      setContactHint(
        center?.recharge?.hint
          || center?.personalQuota?.upgradeContactHint
          || center?.quota?.upgradeContactHint
          || '',
      );
    } catch (e) {
      showToast?.(e?.message || '加载意向信息失败', 'error');
    } finally {
      setBootLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_PURCHASE_INTENT_CONTACT);
      setErrors({});
      return undefined;
    }
    load();
    return undefined;
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  const copySummary = async () => {
    if (!items?.length) return;
    const text = buildCheckoutClipboardText(items, totalYuan);
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('意向清单已复制', 'success');
    } catch {
      showToast?.('复制失败，请手动复制', 'error');
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (submitting || submittingRef.current) {
      showToast?.('正在提交，请勿重复点击', 'error');
      return;
    }
    if (!loggedIn) {
      showToast?.('请先登录后再提交意向', 'error');
      return;
    }
    const built = buildPurchaseIntentSubmitPayload(form, items);
    if (!built.ok) {
      setErrors(built.errors || {});
      showToast?.(built.message || '请检查填写信息', 'error');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await fundingAuthAPI.submitPurchaseIntent(built.payload);
      setForm(EMPTY_PURCHASE_INTENT_CONTACT);
      setErrors({});
      showToast?.(PURCHASE_INTENT_SUBMITTED_TOAST, 'success');
      onSubmitted?.(items);
      onClose?.();
    } catch (e) {
      showToast?.(e?.message || '提交意向失败，请稍后重试', 'error');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!open || !items?.length) return null;

  return createPortal(
    <div className="ipe-recharge-cart-checkout-root" role="presentation" onClick={handleClose}>
      <form
        className="ipe-recharge-cart-checkout"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ipe-recharge-cart-checkout-title"
        autoComplete="off"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <header className="ipe-recharge-cart-checkout__head">
          <div>
            <h2 id="ipe-recharge-cart-checkout-title" className="ipe-recharge-cart-checkout__title">
              提交购买意向
            </h2>
            <p className="ipe-recharge-cart-checkout__subtitle">
              已选 {totalQty} 件，填写公司信息后由商务联系开通
            </p>
          </div>
          <button
            type="button"
            className="ipe-recharge-cart-checkout__close"
            onClick={handleClose}
            aria-label="关闭"
            disabled={submitting}
          >
            <X size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </header>

        <div className="ipe-recharge-cart-checkout__body">
          <ul className="ipe-recharge-cart-checkout__list">
            {items.map((item) => {
              const qty = getCartItemQuantity(item);
              const unitYuan = getCartItemUnitYuan(item);
              const lineYuan = getCartItemLineYuan(item);
              return (
                <li key={item.id || `${item.kind}-${item.title}`} className="ipe-recharge-cart-checkout__line">
                  <div className="ipe-recharge-cart-checkout__line-main">
                    <BillingScopeTag scope={item.scope} />
                    <span className="ipe-recharge-cart-checkout__line-title">{item.title || '—'}</span>
                    <span className="ipe-recharge-cart-checkout__line-meta">{item.summary || item.productTypeLabel || '—'}</span>
                    {qty > 1 ? (
                      <span className="ipe-recharge-cart-checkout__line-qty">×{qty}</span>
                    ) : null}
                  </div>
                  <span className="ipe-recharge-cart-checkout__line-price">
                    ¥{formatYuanAmount(lineYuan)}
                    {qty > 1 ? (
                      <small className="ipe-recharge-cart-checkout__line-unit">（¥{formatYuanAmount(unitYuan)}/件）</small>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="ipe-recharge-cart-checkout__pay ipe-recharge-cart-checkout__pay--intent">
            {/* 冻结挂载点：扫码占位默认关闭，勿删 import / CSS。docs/payment-freeze/README.md */}
            {isScanPayPlaceholderVisible() ? (
              <RechargeScanPayPlaceholder surface="checkout" />
            ) : null}
            <div className="ipe-recharge-cart-checkout__pay-body">
              <p className="ipe-recharge-cart-checkout__pay-price">
                ¥{formatYuanAmount(totalYuan)}
              </p>
              <p className="ipe-recharge-cart-checkout__pay-method">
                {resolvePayMethodHint('checkout')}
              </p>
              {!bootLoading && (recharge?.phone || recharge?.wechat || contactHint) ? (
                <div className="ipe-recharge-cart-checkout__pay-contact">
                  {recharge?.phone ? (
                    <p>
                      <Phone size={14} strokeWidth={1.8} aria-hidden="true" />
                      客服电话：<a href={`tel:${recharge.phone}`}>{recharge.phone}</a>
                    </p>
                  ) : null}
                  {recharge?.wechat ? (
                    <p>
                      <MessageCircle size={14} strokeWidth={1.8} aria-hidden="true" />
                      商务微信：{recharge.wechat}
                    </p>
                  ) : null}
                  {contactHint ? <p>{contactHint}</p> : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="ipe-recharge-cart-checkout__form">
            <p className="ipe-recharge-cart-checkout__form-title">公司信息</p>
            <Field
              id="purchase-intent-company"
              label="公司名称"
              value={form.companyName}
              maxLength={PURCHASE_INTENT_LIMITS.companyNameMax}
              autoComplete="off"
              placeholder="例如：杭州示例科技"
              hint={PURCHASE_INTENT_LIMITS.companyNameHint}
              disabled={submitting || bootLoading}
              error={errors.companyName}
              onChange={(value) => patchField('companyName', value)}
            />
            <Field
              id="purchase-intent-contact-name"
              label="联系人姓名"
              value={form.contactName}
              maxLength={PURCHASE_INTENT_LIMITS.contactNameMax}
              autoComplete="off"
              disabled={submitting || bootLoading}
              error={errors.contactName}
              onChange={(value) => patchField('contactName', value)}
            />
            <Field
              id="purchase-intent-contact-phone"
              label="联系人电话"
              value={form.contactPhone}
              maxLength={11}
              inputMode="tel"
              autoComplete="off"
              disabled={submitting || bootLoading}
              error={errors.contactPhone}
              onChange={(value) => patchField('contactPhone', value.replace(/\D/g, '').slice(0, 11))}
            />
          </div>
        </div>

        <footer className="ipe-recharge-cart-checkout__footer">
          <button type="button" className="ipe-recharge-cart-checkout__copy-btn" onClick={copySummary} disabled={submitting}>
            <Copy size={15} strokeWidth={2} aria-hidden="true" />
            复制清单
          </button>
          <button
            type="submit"
            className="ipe-recharge-cart-checkout__submit"
            disabled={submitting || bootLoading || !items.length}
          >
            {submitting ? '提交中…' : '提交意向'}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}
